import { PenIcon, PlusIcon, TrashIcon } from "@xola/icons";
import React, { useState } from "react";
import { BaseSchedule, BaseScheduleType } from "types";
import { createBaseSchedule } from "../../models/BaseSchedule";
import { BaseScheduleEditorModal } from "../BaseSchedule/BaseScheduleEditorModal";
import { BaseScheduleSummary } from "../BaseSchedule/BaseScheduleSummary";
import { SecondaryButton } from "../Common/SecondaryButton";

interface CouponScheduleEditorProps {
    title: string;
    description: string;
    windowSchedule: BaseSchedule | null;
    blackoutSchedules: BaseSchedule[];
    onWindowScheduleChange: (windowSchedule: BaseSchedule | null) => void;
    onBlackoutSchedulesChange: (blackoutSchedules: BaseSchedule[]) => void;
}

export const CouponScheduleEditor = ({
    title,
    description,
    windowSchedule,
    blackoutSchedules,
    onWindowScheduleChange,
    onBlackoutSchedulesChange,
}: CouponScheduleEditorProps) => {
    const [selectedWindowSchedule, setSelectedWindowSchedule] = useState<BaseSchedule | null>(null);
    const [selectedBlackoutScheduleIndex, setSelectedBlackoutScheduleIndex] = useState<number | null>(null);
    const [showCreateBlackoutScheduleModal, setShowCreateBlackoutScheduleModal] = useState(false);

    const handleCreatePurchaseWindowSchedule = () => {
        setSelectedWindowSchedule(createBaseSchedule({ type: BaseScheduleType.AVAILABLE }));
    };

    const handleUpdatePurchaseWindowSchedule = (schedule: BaseSchedule) => {
        onWindowScheduleChange(schedule);
    };

    const handleRemovePurchaseWindowSchedule = () => {
        onWindowScheduleChange(null);
    };

    const handleCreateBlackoutSchedule = () => {
        setShowCreateBlackoutScheduleModal(true);
    };

    const handleUpdateBlackoutSchedule = (schedule: BaseSchedule) => {
        if (selectedBlackoutScheduleIndex === null) {
            onBlackoutSchedulesChange([...blackoutSchedules, schedule]);
        } else {
            const updatedSchedules = blackoutSchedules.map((blackoutSchedule, index) => {
                return index === selectedBlackoutScheduleIndex ? schedule : blackoutSchedule;
            });

            onBlackoutSchedulesChange(updatedSchedules);
        }
    };

    const handleRemoveBlackoutSchedule = (removeIndex: number) => {
        const updatedSchedules = blackoutSchedules.filter((schedule, index) => index !== removeIndex);
        onBlackoutSchedulesChange(updatedSchedules);
    };

    const selectedBlackoutSchedule =
        selectedBlackoutScheduleIndex === null
            ? createBaseSchedule({ type: BaseScheduleType.UNAVAILABLE })
            : blackoutSchedules[selectedBlackoutScheduleIndex];

    return (
        <div className="space-y-6" data-testid="coupon-schedule-editor-restriction-type">
            <div className="flex items-center justify-between">
                <h5 className="font-bold">Window Definition</h5>

                {windowSchedule ? (
                    <div className="space-x-2">
                        <SecondaryButton
                            size="small"
                            data-testid="coupon-schedule-editor-window-edit-button"
                            onClick={() => {
                                setSelectedWindowSchedule(windowSchedule);
                            }}
                        >
                            <PenIcon />
                        </SecondaryButton>

                        <SecondaryButton
                            size="small"
                            data-testid="coupon-schedule-editor-window-remove-button"
                            onClick={handleRemovePurchaseWindowSchedule}
                        >
                            <TrashIcon />
                        </SecondaryButton>
                    </div>
                ) : null}
            </div>

            {windowSchedule ? (
                <BaseScheduleSummary schedule={windowSchedule} />
            ) : (
                <div className="flex items-center justify-between">
                    <div className="text-gray-darker">No restrictions</div>

                    <SecondaryButton size="small" icon={<PlusIcon />} onClick={handleCreatePurchaseWindowSchedule}>
                        Add {title}
                    </SecondaryButton>
                </div>
            )}

            <h5 className="font-bold">Blackout Restrictions</h5>

            <div>
                {blackoutSchedules.map((schedule, index) => (
                    <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        className="flex items-center justify-between"
                        data-testid="coupon-schedule-editor-blackout-date"
                    >
                        <BaseScheduleSummary showDetails={false} schedule={schedule} />

                        <div className="space-x-2">
                            <SecondaryButton
                                size="small"
                                data-testid="coupon-schedule-editor-blackout-edit-button"
                                onClick={() => {
                                    setSelectedBlackoutScheduleIndex(index);
                                }}
                            >
                                <PenIcon />
                            </SecondaryButton>

                            <SecondaryButton
                                size="small"
                                data-testid="coupon-schedule-editor-blackout-remove-button"
                                onClick={() => {
                                    handleRemoveBlackoutSchedule(index);
                                }}
                            >
                                <TrashIcon />
                            </SecondaryButton>
                        </div>
                    </div>
                ))}

                <div className="flex items-center justify-between">
                    {blackoutSchedules.length === 0 ? <div className="text-gray-darker">No blackouts</div> : null}

                    <SecondaryButton icon={<PlusIcon />} size="small" onClick={handleCreateBlackoutSchedule}>
                        Add Blackout
                    </SecondaryButton>
                </div>
            </div>

            <BaseScheduleEditorModal
                title={title}
                description={description}
                subtitle="Window Definition"
                isOpen={selectedWindowSchedule !== null}
                initialValues={selectedWindowSchedule!}
                onApply={handleUpdatePurchaseWindowSchedule}
                onClose={() => {
                    setSelectedWindowSchedule(null);
                }}
            />

            <BaseScheduleEditorModal
                title={title}
                description={description}
                subtitle="Blackout Restrictions"
                isOpen={showCreateBlackoutScheduleModal || selectedBlackoutScheduleIndex !== null}
                initialValues={selectedBlackoutSchedule}
                onApply={handleUpdateBlackoutSchedule}
                onClose={() => {
                    setSelectedBlackoutScheduleIndex(null);
                    setShowCreateBlackoutScheduleModal(false);
                }}
            />
        </div>
    );
};
